import { useEffect, useMemo, useState } from "react";
import axios from "../api/axios";
import { fetchModerationComments, hideComment, unhideComment, deleteComment } from "../api/moderationApi";
import { extractRoles, resolvePrimaryRole } from "../utils/roleHelpers";
import { notifyDialog, promptReasonDialog } from "../utils/dialogs";

type RoleLiteral = "admin" | "moderator" | "supermoderator" | string;

const allowedRoles: RoleLiteral[] = ["admin", "moderator", "supermoderator"];

export default function ModeratorPanel() {
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [role, setRole] = useState<string>("");
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ username: "", userId: "", contentId: "" });
  const [busy, setBusy] = useState(false);
  const pageSize = 30;

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const res = await axios.get("/auth/me");
        const roles = extractRoles(res.data);
        const normalizedRole = resolvePrimaryRole(roles);
        setRole(normalizedRole);
        const allowed = roles.some((role) => allowedRoles.includes(role));
        setAuthorized(allowed);
        if (allowed) {
          const response = await fetchModerationComments({
            skip: 0,
            take: pageSize,
          });
          setItems(response.items);
          setTotal(response.totalCount);
          setPage(0);
        }
      } catch (error) {
        console.error("[moderation] auth check failed", error);
      } finally {
        setLoading(false);
      }
    };

    void checkAccess();
  }, []);

  const loadComments = async (pageIndex: number, currentRole = role) => {
    setBusy(true);
    try {
      const response = await fetchModerationComments({
        username: filters.username || undefined,
        userId: filters.userId || undefined,
        contentId: filters.contentId || undefined,
        skip: pageIndex * pageSize,
        take: pageSize,
      });
      if (pageIndex === 0) {
        setItems(response.items);
      } else {
        setItems((prev) => (pageIndex === page + 1 ? [...prev, ...response.items] : response.items));
      }
      setTotal(response.totalCount);
      setPage(pageIndex);
      setRole(currentRole);
    } catch (error) {
      console.error("[moderation] load failed", error);
    } finally {
      setBusy(false);
    }
  };

  const handleLoadMore = () => {
    if (items.length >= total) {
      return;
    }
    void loadComments(page + 1);
  };

  const handleAction = async (action: "hide" | "unhide" | "delete", commentId: string) => {
    let reason: string | undefined;
    if (action !== "unhide") {
      const result = await promptReasonDialog({
        title: action === "delete" ? "Yorumu sil" : "Yorumu gizle",
        label: "Gerekçe (opsiyonel)",
        placeholder: "Örn: Spam / uygunsuz içerik",
        confirmText: action === "delete" ? "Evet, sil" : "Gizle",
        danger: action === "delete",
      });
      if (result === null) {
        return;
      }
      reason = result.trim() || undefined;
    }

    setBusy(true);
    const actionMap = {
      hide: hideComment,
      unhide: unhideComment,
      delete: deleteComment,
    } as const;

    try {
      const success = await actionMap[action](commentId, reason);
      if (!success) {
        await notifyDialog("İşlem tamamlanamadı.", "error");
        setBusy(false);
        return;
      }
      await notifyDialog("İşlem başarıyla tamamlandı.", "success");
      await loadComments(page);
    } catch (error) {
      console.error("[moderation] action failed", error);
      await notifyDialog("İşlem sırasında bir hata oluştu.", "error");
    } finally {
      setBusy(false);
    }
  };

  const canDelete = useMemo(() => role === "admin" || role === "supermoderator", [role]);

  if (loading) {
    return <div className="p-10 text-center text-sm text-gray-500">Kontrol ediliyor...</div>;
  }

  if (!authorized) {
    return (
      <div className="p-10 text-center text-sm text-red-500">
        Bu alan için yetkin yok. Moderator/SuperModerator hesabı ile <a href="/nmcdk/moderation/login" className="text-blue-600 underline">giriş yap</a>.
      </div>
    );
  }

  return (
    <div className="space-y-4 bg-white p-6 shadow">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Moderasyon Paneli</h1>
          <p className="text-xs text-slate-500">Yorumları arayabilir, gizleyebilir veya silebilirsin. Silme işlemi sadece Admin / SuperModerator için açıktır.</p>
        </div>
        <button
          type="button"
          onClick={() => void loadComments(0)}
          className="rounded border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100"
        >
          Yenile
        </button>
      </header>

      <div className="grid gap-3 md:grid-cols-3">
        <label className="text-xs uppercase text-slate-500">
          Kullanıcı adı
          <input
            value={filters.username}
            onChange={(event) => setFilters((prev) => ({ ...prev, username: event.target.value }))}
            placeholder="Ara"
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="text-xs uppercase text-slate-500">
          Kullanıcı Id
          <input
            value={filters.userId}
            onChange={(event) => setFilters((prev) => ({ ...prev, userId: event.target.value }))}
            placeholder="GUID"
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="text-xs uppercase text-slate-500">
          İçerik Id
          <input
            value={filters.contentId}
            onChange={(event) => setFilters((prev) => ({ ...prev, contentId: event.target.value }))}
            placeholder="GUID"
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => void loadComments(0)}
          className="rounded bg-slate-900 px-4 py-2 text-xs font-semibold text-white"
        >
          Filtrele
        </button>
        <button
          type="button"
          onClick={() => {
            setFilters({ username: "", userId: "", contentId: "" });
            void loadComments(0);
          }}
          className="rounded border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-600"
        >
          Temizle
        </button>
      </div>

      {busy && <div className="text-xs text-amber-600">İşlem yapılıyor...</div>}

      <div className="space-y-3">
        {items.map((comment) => (
          <div key={comment.id} className="rounded border border-slate-200 p-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">{comment.username}</div>
                <div className="text-xs text-slate-500">
                  {comment.contentTitle || "Seri yok"}
                  {comment.contentPartTitle ? ` · ${comment.contentPartTitle}` : ""}
                </div>
                <div className="text-[11px] text-slate-400">{new Date(comment.createdAt).toLocaleString("tr-TR")}</div>
              </div>
              <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${comment.isHidden ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                {comment.isHidden ? "Gizli" : "Görünür"}
              </span>
            </div>
            <p className="mt-2 whitespace-pre-line text-sm text-slate-700">{comment.text}</p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              {!comment.isHidden && (
                <button
                  type="button"
                  onClick={() => void handleAction("hide", comment.id)}
                  className="rounded border border-amber-400 px-3 py-1 font-semibold text-amber-600"
                >
                  Gizle
                </button>
              )}
              {comment.isHidden && (
                <button
                  type="button"
                  onClick={() => void handleAction("unhide", comment.id)}
                  className="rounded border border-emerald-400 px-3 py-1 font-semibold text-emerald-600"
                >
                  Göster
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  onClick={() => void handleAction("delete", comment.id)}
                  className="rounded border border-rose-400 px-3 py-1 font-semibold text-rose-600"
                >
                  Sil
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {items.length === 0 && <div className="text-center text-sm text-slate-500">Kayıt bulunamadı.</div>}

      {items.length < total && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={handleLoadMore}
            className="rounded border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-600"
          >
            Devamını getir
          </button>
        </div>
      )}
    </div>
  );
}
